<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('product.info.create_service')"
        :edit-mode-text="$t('product.info.edit_service')"
        @reset="editExcursionServiceId = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :headers="useHeaders"
                :items="useFilteredServices"
                :items-per-page="-1"
                :items-per-page-options="[-1]"
                :loading="isLoading"
                :items-length="useFilteredServices.length"
                :custom-key-sort="{
                    name: (a, b) => localized(a).localeCompare(localized(b)),
                }"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="guests-table"
            >
                <template v-slot:item.name="{item}">
                    <div class="text-link" @click="editExcursionService(item.id)">
                        {{ localized(item.name) }}
                    </div>
                </template>
                <template v-slot:item.excursionCode="{item}">
                    {{ item.excursionCode ? item.excursionCode.name : '' }}
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <InfoExcursionServiceForm
                :edit-excursion-service-id="editExcursionServiceId"
                :contract-id="contractId"
                @save="save()"
                @cancel="modeSwitcher.goToTable()"
            />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import InfoExcursionServiceForm from '~/components/products/information/InfoExcursionServiceForm.vue'
    import {useCatalogue} from '~/composables/form-helpers'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const excursionServices = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editExcursionServiceId = ref<number>(null)
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
        if (!useCatalogue.value?.length) await supplierStore.loadCatalogue()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            const {services} = await app.$api.ownExcursionServices.get(rq)
            excursionServices.value = services
        } catch (e) {
            excursionServices.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('time'), value: 'startTime'},
            {title: t('excursion_code'), value: 'excursionCode'},
            {title: t('escort_language'), value: 'escortLanguage'},
            {title: t('type'), value: 'escortType'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const useItems = computed(() => {
        return excursionServices.value.map(({id, name, startTime, active, categoryId, accompanimentInfos}) => ({
            id,
            name,
            startTime,
            active,
            excursionCode: useCatalogue.value.find(({id}) => id == categoryId) || {},
            escortLanguage: accompanimentInfos
                .map(({languageCode}) => (languageCode ? languageCode : t('without_escort')))
                .join(', '),
            escortType: accompanimentInfos.map(({type}) => t(`escortType.${type}`)).join(', '),
        }))
    })

    const editExcursionService = (id: number): void => {
        modeSwitcher.value.edit()
        editExcursionServiceId.value = id
    }

    const useFilteredServices = computed(() => {
        return useItems.value.filter(({active}) => activeFilter.value === active)
    })
</script>
